import { supabase } from '@tickitacka/shared';
import { queryClient } from '@/queryClient';
import { RealtimeChannel } from '@supabase/supabase-js';
import { create } from 'zustand';
import { Notification } from '@/common/types/databaseTypes';
import {
    CommentContent,
    getDefaultRelatedType,
    NotificationType,
    MentionContent,
} from '@/features/notification/constants/notification';
import { FEEDBACK_KEYS } from '@/features/performance/feedback/services/useFeedbackQueries';

interface NotificationStoreState {
    // 상태
    unreadCount: number;
    notificationSubscription: RealtimeChannel | null;
    notificationDeletionSubscription: RealtimeChannel | null;

    // 액션
    updateUnreadCount: (userId: string) => Promise<void>;
    subscribeToNotifications: (userId: string) => Promise<void>;
    subscribeToNotificationDeletion: (userId: string) => Promise<void>;
    unsubscribeFromNotifications: () => Promise<void>;
    unsubscribeFromNotificationDeletion: () => Promise<void>;
}

export const useNotificationStore = create<NotificationStoreState>(
    (set, get) => ({
        unreadCount: 0,
        notificationSubscription: null,
        notificationDeletionSubscription: null,

        updateUnreadCount: async (userId: string) => {
            const { data: unreadData } = await supabase
                .from('notification')
                .select('id')
                .eq('user_id', userId)
                .eq('is_read', false)
                .eq('enable', true);

            set({ unreadCount: unreadData?.length || 0 });
        },

        subscribeToNotifications: async (userId: string) => {
            // 기존 구독이 있으면 해제
            await get().unsubscribeFromNotifications();
            // 초기 카운트 설정
            await get().updateUnreadCount(userId);

            const subscription = supabase
                .channel('notification_changes')
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'notification',
                        filter: `user_id=eq.${userId}`,
                    },
                    payload => {
                        // 1. unreadCount 업데이트
                        void get().updateUnreadCount(userId);

                        // 2. 알림 관련 데이터 캐시 무효화
                        const updatedNotification = payload.new as Notification;
                        const notificationType =
                            updatedNotification.type as NotificationType;

                        switch (getDefaultRelatedType(notificationType)) {
                            case 'workspace': {
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'invitations',
                                        'workspace',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'invitations',
                                        'team',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'workspace',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: ['team'],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: ['myTeams'],
                                });
                                break;
                            }
                            case 'team': {
                                void queryClient.invalidateQueries({
                                    queryKey: ['workspace'],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'team',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: ['myTeams'],
                                });
                                break;
                            }

                            case 'goal':
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'goal',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                break;
                            case 'project':
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'project',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: ['projects'],
                                });
                                break;
                            case 'todo':
                                void queryClient.invalidateQueries({
                                    queryKey: [
                                        'todo',
                                        updatedNotification?.related_id,
                                    ],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: ['todos', userId],
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: ['planners', userId],
                                });
                                break;
                            case 'comment': {
                                const notificationContent =
                                    updatedNotification.content as CommentContent;

                                switch (notificationContent.parent_type) {
                                    case 'goal':
                                        // 댓글 캐시 무효화
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'goalComments',
                                                notificationContent.parent_id,
                                            ],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'activityLogs',
                                                notificationContent.parent_id,
                                            ],
                                        });

                                        // 댓글 개수 캐시 무효화
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'goal',
                                                notificationContent.parent_id,
                                            ],
                                        });
                                        break;
                                    case 'todo':
                                        // 댓글 캐시 무효화
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'todoComments',
                                                notificationContent.parent_id,
                                            ],
                                        });
                                        // 댓글 개수 캐시 무효화
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'todo',
                                                notificationContent.parent_id,
                                            ],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: ['todos'],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: ['planners'],
                                        });
                                        break;
                                    case 'project':
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'project',
                                                notificationContent.parent_id,
                                            ],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: ['projects'],
                                        });
                                        break;
                                    default:
                                        break;
                                }
                                // void queryClient.invalidateQueries({
                                //     queryKey: ['comments', userId],
                                // });
                                break;
                            }
                            case 'note': {
                                const notificationContent =
                                    updatedNotification.content as MentionContent;

                                switch (notificationContent.context_type) {
                                    case 'goal':
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'goal',
                                                notificationContent.context_id,
                                            ],
                                        });
                                        break;
                                    case 'todo':
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'todo',
                                                notificationContent.context_id,
                                            ],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: ['todos', userId],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: ['planners', userId],
                                        });
                                        break;
                                    case 'project':
                                        void queryClient.invalidateQueries({
                                            queryKey: [
                                                'project',
                                                notificationContent.context_id,
                                            ],
                                        });
                                        void queryClient.invalidateQueries({
                                            queryKey: ['projects'],
                                        });
                                        break;
                                    default:
                                        break;
                                }
                                // void queryClient.invalidateQueries({
                                //     queryKey: ['notes', userId],
                                // });
                                break;
                            }
                            case 'feedback':
                                // 피드백 및 요청 목록 갱신 (요청에 대한 응답일 경우 처리)
                                void queryClient.invalidateQueries({
                                    queryKey: FEEDBACK_KEYS.all,
                                });
                                void queryClient.invalidateQueries({
                                    queryKey: FEEDBACK_KEYS.requestAll,
                                });
                                break;
                            case 'feedback_request':
                                void queryClient.invalidateQueries({
                                    queryKey: FEEDBACK_KEYS.requestAll,
                                });
                                break;
                            case 'feedback_comment':
                                void queryClient.invalidateQueries({
                                    queryKey: FEEDBACK_KEYS.all,
                                });
                                break;
                            default:
                                break;
                        }

                        // 3. 알림 캐시 무효화
                        void queryClient.invalidateQueries({
                            queryKey: ['notifications', userId],
                        });
                    }
                )
                .subscribe();

            set({ notificationSubscription: subscription });
        },

        unsubscribeFromNotifications: async () => {
            const { notificationSubscription } = get();
            if (notificationSubscription) {
                await notificationSubscription.unsubscribe();
                set({ notificationSubscription: null });
            }
        },

        subscribeToNotificationDeletion: async (userId: string) => {
            // 기존 구독이 있으면 해제
            await get().unsubscribeFromNotificationDeletion();

            const subscription = supabase
                .channel('notification_deletion')
                .on(
                    'postgres_changes',
                    {
                        event: 'DELETE',
                        schema: 'public',
                        table: 'notification',
                    },
                    payload => {
                        // 1. unreadCount 업데이트
                        void get().updateUnreadCount(userId);

                        // 2. React Query 캐시 무효화
                        void queryClient.invalidateQueries({
                            queryKey: ['notifications', userId],
                        });
                    }
                )
                .subscribe();

            set({ notificationDeletionSubscription: subscription });
        },

        unsubscribeFromNotificationDeletion: async () => {
            const { notificationDeletionSubscription } = get();
            if (notificationDeletionSubscription) {
                await notificationDeletionSubscription.unsubscribe();
                set({ notificationDeletionSubscription: null });
            }
        },
    })
);
